import { router, usePage } from '@inertiajs/react'
import { Download, SlidersHorizontal, X } from 'lucide-react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  reportsDepartmentsCsvPath,
  reportsDepartmentsPdfPath,
  reportsDepartmentsPath,
} from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebReportDepartmentRow from '@/shared/types/reports/WebReportDepartmentRow'
import { ReportEmptyState } from '../components/ReportEmptyState'
import { ReportMobileField } from '../components/ReportMobileField'
import { ReportRangeOrderError } from '../components/ReportRangeOrderError'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import { useEffect, useRef, useState } from 'react'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import { downloadExport } from '@/shared/lib/downloadExport'

interface FiltersShape {
  fromDate?: string
  toDate?: string
}

interface Props {
  rows: WebReportDepartmentRow[]
  meta: PaginationMeta
  filters: FiltersShape
  defaultFromDate: string
  defaultToDate: string
  [key: string]: unknown
}

const DepartmentsReportPage = () => {
  const { rows, meta, filters, defaultFromDate, defaultToDate, complianceSettings } = usePage<
    Props & SharedProps
  >().props

  useBreadcrumbs([{ label: t('nav.departmentsReport') }])

  const [fromDate, setFromDate] = useState(filters.fromDate ?? defaultFromDate)
  const [toDate, setToDate] = useState(filters.toDate ?? defaultToDate)
  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()

  /* eslint-disable react-hooks/set-state-in-effect -- mirror URL-driven Inertia props (camelCase) */
  useEffect(() => {
    setFromDate(filters.fromDate ?? defaultFromDate)
    setToDate(filters.toDate ?? defaultToDate)
  }, [filters.fromDate, filters.toDate, defaultFromDate, defaultToDate])
  /* eslint-enable react-hooks/set-state-in-effect */

  const dateRangeInvalid = isReportRangeOrderInvalid(fromDate, toDate)

  const buildFilterParams = (overrides: { fromDate?: string; toDate?: string } = {}) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    const params: Record<string, string> = {}
    if (fd) params.from_date = fd
    if (td) params.to_date = td
    return params
  }

  const navigate = (
    overrides: { fromDate?: string; toDate?: string } = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    if (isReportRangeOrderInvalid(fd, td)) return
    const params = buildFilterParams(overrides)
    const visit = beginVisit(
      JSON.stringify(params),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    router.get(reportsDepartmentsPath(), params, {
      preserveState: visit.keepDropdownOpen,
      preserveScroll: visit.keepDropdownOpen,
    })
  }

  const handleFromDateChange = (val: string) => {
    setFromDate(val)
    navigate({ fromDate: val })
  }

  const handleToDateChange = (val: string) => {
    setToDate(val)
    navigate({ toDate: val })
  }

  const clearFilters = () => {
    setFromDate(defaultFromDate)
    setToDate(defaultToDate)
    router.get(reportsDepartmentsPath(), {}, { preserveState: false })
  }

  const handlePageChange = (page: number) => {
    if (dateRangeInvalid) return
    router.get(
      reportsDepartmentsPath(),
      { ...buildFilterParams(), page: String(page) },
      { preserveState: false },
    )
  }

  const handleExportCsv = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsDepartmentsCsvPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const handleExportPdf = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsDepartmentsPdfPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  const activeFilterCount = [fromDate !== defaultFromDate, toDate !== defaultToDate].filter(
    Boolean,
  ).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.departments.fromDate')}</Label>
        <DatePickerInput
          value={fromDate}
          onChange={handleFromDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.departments.toDate')}</Label>
        <DatePickerInput
          value={toDate}
          onChange={handleToDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>
      <ReportRangeOrderError show={dateRangeInvalid} />
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="min-w-0 text-xl font-bold sm:text-2xl">{t('reports.departments.title')}</h1>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.departments.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPdf}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.departments.exportPdf')}
          </Button>

          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                if (!open) navigate({}, 'sheet-close')
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('reports.departments.filters')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('reports.departments.filters')}
                hideCloseButton
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-semibold">{t('reports.departments.filters')}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('reports.departments.clearFilters')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('reports.departments.filters')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {filterFields}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('reports.departments.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <ReportEmptyState message={t('reports.departments.noRecords')} />
      ) : (
        <div className="space-y-4">
          <div className="space-y-2 lg:hidden">
            {rows.map((row) => (
              <div key={row.id} className="rounded-lg border bg-card p-3 shadow-sm">
                <p className="font-medium text-foreground [overflow-wrap:anywhere]">{row.name}</p>
                {row.siteName ? (
                  <p className="mt-0.5 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                    {row.siteName}
                  </p>
                ) : null}
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <ReportMobileField label={t('reports.departments.colInstruments')}>
                    {row.instrumentCount}
                  </ReportMobileField>
                  <ReportMobileField label={t('reports.departments.colAvgCompliance')}>
                    <ComplianceBadge
                      percent={row.avgCompliancePercent}
                      colorCodes={complianceSettings?.colorCodes}
                    />
                  </ReportMobileField>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('reports.departments.colDepartment')}</TableHead>
                    <TableHead>{t('reports.departments.colSite')}</TableHead>
                    <TableHead className="text-right">
                      {t('reports.departments.colInstruments')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('reports.departments.colAvgCompliance')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell className="text-muted-foreground">{row.siteName}</TableCell>
                      <TableCell className="text-right">{row.instrumentCount}</TableCell>
                      <TableCell className="text-right">
                        <ComplianceBadge
                          percent={row.avgCompliancePercent}
                          colorCodes={complianceSettings?.colorCodes}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <DataPagination
            meta={meta}
            onPageChange={handlePageChange}
            noun={t('reports.departments.paginationNoun')}
          />
        </div>
      )}
    </div>
  )
}

export default DepartmentsReportPage
